/**
 * TRUS-638 — pinned channels and DMs in the Inbox left panel.
 *
 * Pins are per user and per org (server: messaging_channel_pins). The channel
 * list (`['messaging-channels', search]`) carries `is_pinned` / `pin_position`;
 * every mutation here updates ALL of those cached lists optimistically, rolls
 * them back on error with a toast, and re-syncs from the server when it settles.
 */

import { useCallback, useSyncExternalStore } from 'react';
import { useMutation, useQueryClient, type QueryClient, type QueryKey } from '@tanstack/react-query';
import { messagingApi, Channel } from '@/services/messagingApi';
import { useAppTranslation } from '@/contexts/TranslationContext';
import { toast } from '../ui/use-toast';
import { errorCode } from './errorDetail';

export const MAX_PINS = 20;
const LIST_KEY = ['messaging-channels'] as const;

/** Pinned channels from a list, top first. */
export function sortedPins(channels: Channel[]): Channel[] {
  return channels
    .filter((c) => c.is_pinned)
    .sort((a, b) => (a.pin_position ?? Number.MAX_SAFE_INTEGER) - (b.pin_position ?? Number.MAX_SAFE_INTEGER));
}

type Snapshot = Array<[QueryKey, Channel[] | undefined]>;

function patchLists(qc: QueryClient, update: (list: Channel[]) => Channel[]): Snapshot {
  const snapshot = qc.getQueriesData<Channel[]>({ queryKey: LIST_KEY }) as Snapshot;
  qc.setQueriesData<Channel[]>({ queryKey: LIST_KEY }, (old) => (Array.isArray(old) ? update(old) : old));
  return snapshot;
}

function restore(qc: QueryClient, snapshot?: Snapshot) {
  snapshot?.forEach(([key, data]) => qc.setQueryData(key, data));
}

/** Whether `channelId` is pinned, according to whichever channel list is cached. */
export function useIsChannelPinned(channelId: number): boolean {
  const qc = useQueryClient();
  const subscribe = useCallback((cb: () => void) => qc.getQueryCache().subscribe(cb), [qc]);
  const get = () => {
    for (const [, data] of qc.getQueriesData<Channel[]>({ queryKey: LIST_KEY })) {
      const hit = Array.isArray(data) ? data.find((c) => c.id === channelId) : undefined;
      if (hit) return hit.is_pinned === true;
    }
    return false;
  };
  return useSyncExternalStore(subscribe, get, get);
}

export function useChannelPins() {
  const { t } = useAppTranslation();
  const qc = useQueryClient();

  const settle = () => qc.invalidateQueries({ queryKey: LIST_KEY });
  const fail = (fallback: string) => (error: Error, _vars: unknown, ctx?: { snapshot?: Snapshot }) => {
    restore(qc, ctx?.snapshot);
    const title =
      errorCode(error) === 'pin_limit'
        ? t('messaging.pins.limit', 'You can pin up to {{max}} conversations', { max: MAX_PINS })
        : fallback;
    toast({ title, variant: 'destructive' });
  };

  const pin = useMutation({
    mutationFn: (channelId: number) => messagingApi.pinChannel(channelId),
    onMutate: async (channelId: number) => {
      await qc.cancelQueries({ queryKey: LIST_KEY });
      const snapshot = patchLists(qc, (list) => {
        const next = list.reduce((m, c) => (c.is_pinned ? Math.max(m, (c.pin_position ?? -1) + 1) : m), 0);
        return list.map((c) => (c.id === channelId && !c.is_pinned ? { ...c, is_pinned: true, pin_position: next } : c));
      });
      return { snapshot };
    },
    onError: fail(t('messaging.pins.pinFailed', 'Could not pin conversation')),
    onSettled: settle,
  });

  const unpin = useMutation({
    mutationFn: (channelId: number) => messagingApi.unpinChannel(channelId),
    onMutate: async (channelId: number) => {
      await qc.cancelQueries({ queryKey: LIST_KEY });
      const snapshot = patchLists(qc, (list) =>
        list.map((c) => (c.id === channelId ? { ...c, is_pinned: false, pin_position: null } : c)),
      );
      return { snapshot };
    },
    onError: fail(t('messaging.pins.unpinFailed', 'Could not unpin conversation')),
    onSettled: settle,
  });

  const reorder = useMutation({
    mutationFn: (channelIds: number[]) => messagingApi.reorderPins(channelIds),
    onMutate: async (channelIds: number[]) => {
      await qc.cancelQueries({ queryKey: LIST_KEY });
      const rank = new Map(channelIds.map((id, i) => [id, i]));
      const snapshot = patchLists(qc, (list) =>
        list.map((c) => (c.is_pinned && rank.has(c.id) ? { ...c, pin_position: rank.get(c.id)! } : c)),
      );
      return { snapshot };
    },
    onError: fail(t('messaging.pins.reorderFailed', 'Could not reorder pinned conversations')),
    onSettled: settle,
  });

  /** Move a pinned channel by `delta` places within `pins` (top first). */
  const move = (pins: Channel[], channelId: number, delta: number) => {
    const ids = pins.map((c) => c.id);
    const from = ids.indexOf(channelId);
    const to = from + delta;
    if (from < 0 || to < 0 || to >= ids.length) return;
    ids.splice(to, 0, ids.splice(from, 1)[0]);
    reorder.mutate(ids);
  };

  /** Drop `channelId` onto `targetId`'s place within `pins`. */
  const moveTo = (pins: Channel[], channelId: number, targetId: number) => {
    const ids = pins.map((c) => c.id);
    const from = ids.indexOf(channelId);
    const to = ids.indexOf(targetId);
    if (from < 0 || to < 0 || from === to) return;
    ids.splice(to, 0, ids.splice(from, 1)[0]);
    reorder.mutate(ids);
  };

  return { pin, unpin, reorder, move, moveTo };
}
